import { useCallback } from 'react';
import { pick } from 'ramda';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { userSetAttributesAction, userUnsetAttributesAction } from 'js/hooks/user/store/actions';

import * as pas from '../pas';
import { rfidLogin } from 'js/app/hooks/auth/pas';
import * as helpers from 'js/hooks/auth/helpers';
import * as actions from 'js/hooks/auth/store/actions';
import { ERROR_KEY_TERMS } from 'js/app/hooks/auth/constants';
import { useGetLoginMode } from 'js/hooks/auth/hooks/useGetLoginMode';
import { useLoginRequest } from 'js/hooks/auth/hooks/useLoginRequest';
import { useValidateSession } from 'js/hooks/auth/hooks/useValidateSession/useValidateSession';

import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useUserSelector, useAuthSelector } from 'js/store/hooks';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useBeforeLoginAction } from 'js/hooks/auth/hooks/useBeforeLoginAction';
import { useCloseLoginPopup } from 'js/components/popups/LoginPopup/buzz/hooks/useCloseLoginPopup';

export const useInitialLoginAndSessionValidation = () => {
    const dispatch = useDispatch();
    const request = useLoginRequest();
    const getLoginMode = useGetLoginMode();
    const validateSession = useValidateSession();
    const closeLoginPopup = useCloseLoginPopup();
    const beforeLoginAction = useBeforeLoginAction();
    const { role, isLoggedIn } = useUserSelector(pick(['role', 'isLoggedIn']));
    const { isLoginInProcess, isLogoutInProcess } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess']));

    // buzz line
    const [dTagLegacyCardStartingNumber] = useDynamicTags([DYNAMIC_TAG_NAMES.LegacyCardStartingNumber]);

    return useCallback(async (inputUserName, password, rememberMeLogin, afterRegisterLogin, loginMode) => {
        if (isLoggedIn) {
            return;
        }

        if (role === 'admin') {
            return Promise.reject({ errorMessage: 'Admin can\'t login to portal' });
        }

        if (isLoginInProcess || isLogoutInProcess || !request) {
            return Promise.reject();
        }

        dispatch(actions.setIsLoginInProcess(true));
        beforeLoginAction();

        try {
            const { languageCode } = request;
            const userNameLoginMode = getLoginMode(inputUserName);
            const initialCredentials = {
                userName: inputUserName,
                password,
                loginMode: loginMode || userNameLoginMode,
                languageCode,
                rememberMeLogin,
                dTagLegacyCardStartingNumber
            };

            // Login with card flow for cards not starting from 7
            const isCardLoginMode = loginMode === 'userName' && inputUserName[0] !== '7';

            dispatch(userUnsetAttributesAction('relogin'));
            dispatch(userUnsetAttributesAction('reloginCredentials'));

            const pasLoginResponse = isCardLoginMode
                ? await rfidLogin(initialCredentials)
                : await pas.login(initialCredentials);

            const isSeparateSessionTokenNeeded = isCardLoginMode
                && pasLoginResponse.data.errorCode === 0
                && pasLoginResponse.data.username
                && !pasLoginResponse.data.sessionValidationData;
            const sessionTokenResponse = isSeparateSessionTokenNeeded ? await pas.getTempToken() : false;

            const {
                userId,
                loginName,
                userName,
                sessionToken
            } = helpers.parsePasLoginResponse(pasLoginResponse, sessionTokenResponse);
            const credentials = { ...initialCredentials, userName, loginName, userId, inputUserName };

            dispatch(userSetAttributesAction({
                loginName,
                userId,
                userName,
                password,
                rememberMeLogin
            }));

            let isProceedLoginInNative = false;

            try {
                isProceedLoginInNative = await helpers.externalLoginAction({ ...credentials, sessionToken, afterRegisterLogin });
            } catch (error) {
                dispatch(actions.setIsLoginInProcess(false));

                return Promise.reject(error);
            }

            if (!validateSession(pasLoginResponse, credentials)) {
                dispatch(actions.setIsLoginInProcess(false));
                dispatch(userSetAttributesAction({
                    relogin: true,
                    reloginCredentials: { ...credentials }
                }));

                dispatcher.dispatch('auth:login:failed:withPopup');
                closeLoginPopup();

                if (pasLoginResponse.data.sessionValidationData?.SessionValidationByTCVersionData) {
                    pasLoginResponse.data.errorCode = ERROR_KEY_TERMS;
                }

                return Promise.reject(pasLoginResponse);
            }

            if (!sessionToken) {
                dispatch(actions.setIsLoginInProcess(false));
                dispatcher.dispatch('auth:credentials:login:failed', 'Login error');

                return Promise.reject(pasLoginResponse);
            }

            if (!rememberMeLogin) {
                helpers.removeRememberMeItem();
            }

            return { ...credentials, sessionToken, isProceedLoginInNative };
        } catch (error) {
            dispatch(actions.setIsLoginInProcess(false));
            dispatcher.dispatch('auth:credentials:login:failed', error?.data?.playerMessage || 'Login error');

            throw error;
        }
    }, [
        isLoggedIn, role, isLoginInProcess, isLogoutInProcess, request, dispatch,
        beforeLoginAction, getLoginMode, dTagLegacyCardStartingNumber, validateSession, closeLoginPopup
    ]);
};
